"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  Mail,
  MessageCircle,
  FileText,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import Image from "next/image";
import { CrossSellWidget } from "@/components/cross-sell-widget";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.5, ease: "easeOut" },
  }),
};

// ============================================================
// Conteúdo por modo. Default = Mapa de Contexto (tripwire principal,
// leva à Jornada de entrevista com o Jhow). Fallback = genérico pros
// demais produtos que redirecionam pra /obrigado.
// ============================================================
type Modo = "mapa" | "generico";

const CONTEUDO = {
  mapa: {
    titulo: "Pronto. Seu Mapa de Contexto começa agora.",
    sub: "Você não comprou um curso pra assistir. Comprou uma conversa que vira o raio-X do seu negócio.",
    autorMsg:
      "Em cerca de 15 minutos com o Jhow, seu Mapa de Contexto fica pronto. É por ele que tudo o que você criar depois vai nascer alinhado.",
    passos: [
      {
        icon: Mail,
        titulo: "Confirme seu acesso",
        descricao:
          "Enviamos um email com os detalhes do seu acesso. Confira também o spam. Você já pode começar por aqui.",
      },
      {
        icon: MessageCircle,
        titulo: "Converse com o Jhow",
        descricao:
          "Uma entrevista guiada de poucos minutos. O Jhow te faz as perguntas certas e monta seu Mapa de Contexto na hora.",
      },
      {
        icon: FileText,
        titulo: "Receba seu Mapa",
        descricao:
          "O raio-X do seu negócio: posicionamento, segmento, persona e a Big Idea. Fica salvo na sua biblioteca pra usar em tudo.",
      },
    ],
    ctaLabel: "Começar minha Jornada com o Jhow",
    ctaHref: "/app/jornada",
  },
  generico: {
    titulo: "Pronto. Sua compra foi confirmada.",
    sub: "Você deu o primeiro passo. Enviamos pro seu email tudo o que precisa pra começar.",
    autorMsg:
      "Bem-vindo ao eBuz. Vamos transformar seu conhecimento em um produto educacional de verdade.",
    passos: [
      {
        icon: Mail,
        titulo: "Confirme seu acesso",
        descricao:
          "Enviamos um email com os detalhes do seu acesso. Confira também a caixa de spam.",
      },
      {
        icon: CheckCircle2,
        titulo: "Entre na plataforma",
        descricao:
          "Use o email desta compra pra acessar o eBuz e tudo o que você contratou.",
      },
      {
        icon: Sparkles,
        titulo: "Comece agora",
        descricao:
          "Está tudo liberado. Dê o primeiro passo direto pela plataforma.",
      },
    ],
    ctaLabel: "Acessar minha conta",
    ctaHref: "/app",
  },
} as const;

function ResendWelcomeBlock() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "done">("idle");
  async function handleResend() {
    if (!email || status === "sending") return;
    setStatus("sending");
    try {
      await fetch("/api/auth/resend-welcome", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
    } catch {}
    setStatus("done");
  }
  return (
    <div className="mt-10 rounded-xl border border-brand-100 bg-white p-5 text-left">
      <p className="text-sm font-semibold text-gray-900 mb-1 font-sora">
        Não recebeu o email de acesso?
      </p>
      <p className="text-sm text-gray-500 mb-3">
        Confira o spam. Se não chegou, reenviamos agora:
      </p>
      {status === "done" ? (
        <p className="text-sm text-brand-600 flex items-center gap-1.5">
          <CheckCircle2 className="h-4 w-4" /> Se existir uma conta com esse email, reenviamos as instruções.
        </p>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="seu@email.com"
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
          <button
            onClick={handleResend}
            disabled={status === "sending"}
            className="rounded-lg bg-brand-500 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60 transition"
          >
            {status === "sending" ? "Enviando..." : "Reenviar email"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function ObrigadoPage() {
  const [modo, setModo] = useState<Modo>("mapa");

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const product = (q.get("product") || "").toLowerCase();
    const ref = (q.get("ref") || "").toLowerCase();
    const outros = ["maquina-livros", "combo", "comunidade", "seo-geo", "livro", "low-ticket"];
    if (outros.some((o) => product.includes(o) || ref.includes(o))) {
      setModo("generico");
    }
  }, []);

  const c = CONTEUDO[modo];

  return (
    <div className="bg-[#fdf8f9] font-manrope">
      <section className="mx-auto max-w-2xl px-4 pt-10 pb-20 md:pt-16 text-center">
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-brand-50">
            <Sparkles className="h-10 w-10 text-brand-500" />
          </div>
        </motion.div>

        <motion.h1
          className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 font-sora"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={1}
        >
          {c.titulo}
        </motion.h1>

        <motion.p
          className="text-lg text-gray-600 mb-8"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={2}
        >
          {c.sub}
        </motion.p>

        <motion.div
          className="flex items-center gap-4 rounded-xl bg-brand-50 border border-brand-100 p-5 mb-12 mx-auto max-w-md"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={2.5}
        >
          <Image
            src="/images/jonathan-machado.jpg"
            alt="Jonathan Machado"
            width={64}
            height={64}
            className="rounded-full border-2 border-brand-100 shrink-0"
          />
          <div className="text-left">
            <p className="font-semibold text-gray-900 font-sora">Jonathan Machado</p>
            <p className="text-sm text-gray-600">{c.autorMsg}</p>
          </div>
        </motion.div>

        <motion.div
          className="space-y-4 mb-12 text-left"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={3}
        >
          {c.passos.map((step, i) => {
            const Icon = step.icon;
            return (
              <div
                key={i}
                className="flex gap-4 rounded-xl bg-white p-5 border border-gray-100"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 shrink-0">
                  <Icon className="h-5 w-5 text-brand-500" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-500 text-[11px] font-bold text-white">
                      {i + 1}
                    </span>
                    <h3 className="font-semibold text-gray-900 font-sora">{step.titulo}</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{step.descricao}</p>
                </div>
              </div>
            );
          })}
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4}>
          <a
            href={c.ctaHref}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-grad px-8 py-4 text-white font-semibold text-lg hover:opacity-95 active:scale-[0.98] transition-all shadow-lg shadow-brand-500/25 font-sora"
          >
            {c.ctaLabel}
            <ArrowRight className="h-5 w-5" />
          </a>
        </motion.div>

        <motion.p
          className="mt-8 flex items-center justify-center gap-1.5 text-sm text-gray-400"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={5}
        >
          <CheckCircle2 className="h-4 w-4" />
          Você também pode acessar pelo link enviado no seu email
        </motion.p>

        <ResendWelcomeBlock />

        <div className="mt-12 text-left">
          <CrossSellWidget
            context="after_purchase"
            maxItems={3}
            variant="cards"
            title="Continue sua jornada"
          />
        </div>
      </section>
    </div>
  );
}
